/**
 * VP Service Parts Page — JUL-139
 * Search, multi-select filters, bottom drawer (mobile), sticky bar, product grid filtering.
 */
(function () {
  'use strict';

  const ROOT_SEL = '.vp-spp';
  const BREAKPOINT = 750;

  /* ── helpers ── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const isDesktop = () => window.innerWidth >= BREAKPOINT;

  /* ── init ── */
  function init() {
    const root = $(ROOT_SEL);
    if (!root) return;

    const state = {
      keywords: '',
      machines: new Set(),
      collections: new Set(),
    };

    /* Elements */
    const items = $$('.vp-spp__product-item', root);
    const grid = $('.vp-spp__grid', root);
    const noResults = $('.vp-spp__no-results', root);

    /* Desktop elements */
    const dKeywords = $('.vp-spp__seg-input--keywords', root);
    const dMachinesTrigger = $('.vp-spp__seg-trigger--machines', root);
    const dCollTrigger = $('.vp-spp__seg-trigger--collections', root);
    const dMachinesDropdown = $('.vp-spp__dropdown--machines', root);
    const dCollDropdown = $('.vp-spp__dropdown--collections', root);
    const dClear = $('.vp-spp__clear-btn--desktop', root);

    /* Mobile elements */
    const mInput = $('.vp-spp__mobile-input', root);
    const drawer = $('.vp-spp__drawer', root);
    const overlay = $('.vp-spp__overlay', root);
    const drawerClose = $('.vp-spp__drawer-close', root);
    const mKeywords = $('.vp-spp__drawer-input--keywords', root);
    const mMachinesTrigger = $('.vp-spp__drawer-seg-trigger--machines', root);
    const mCollTrigger = $('.vp-spp__drawer-seg-trigger--collections', root);
    const mClear = $('.vp-spp__clear-btn--mobile', root);

    /* Mobile full-screen panels */
    const mMachinesPanel = $('.vp-spp__panel--machines', root);
    const mCollPanel = $('.vp-spp__panel--collections', root);
    const mMachinesPanelList = $('.vp-spp__panel-list--machines', root);
    const mCollPanelList = $('.vp-spp__panel-list--collections', root);
    const mMachinesPanelSearch = $('.vp-spp__panel-search-input--machines', root);
    const mCollPanelSearch = $('.vp-spp__panel-search-input--collections', root);

    /* Sticky elements */
    const stickyWrapper = $('.vp-spp__sticky', root);
    const title = $('.vp-spp__title', root);

    /* ── Collection GID → title map (rendered by Liquid) ── */
    let colMap = {};
    try {
      const mapEl = document.getElementById('vp-spp-col-map');
      if (mapEl) colMap = JSON.parse(mapEl.textContent);
    } catch { /* ignore */ }

    function resolveCollectionName(raw) {
      /* raw may be a GID like "gid://shopify/Collection/12345" or already a title */
      const match = raw.match(/\/(\d+)$/);
      if (match) {
        return colMap[match[1]] || colMap[Number(match[1])] || raw;
      }
      return raw;
    }

    /* ── Build filter options from product data attributes ── */
    const allMachines = new Set();
    const allCollections = new Set();
    const productMeta = new Map();

    items.forEach((item) => {
      const id = item.dataset.productId;
      const titleText = (item.dataset.title || '').toLowerCase();
      const machines = (item.dataset.machines || '')
        .split('|||')
        .map((s) => s.trim())
        .filter(Boolean);
      const colls = (item.dataset.collections || '')
        .split('|||')
        .map((s) => resolveCollectionName(s.trim()))
        .filter(Boolean);

      machines.forEach((m) => allMachines.add(m));
      colls.forEach((c) => allCollections.add(c));

      productMeta.set(id, {
        title: titleText,
        machines,
        collections: colls,
      });
    });

    /* Sort collections by collection_order if available */
    let sortedCollections;
    try {
      const orderEl = document.getElementById('vp-spp-collection-order');
      const order = orderEl ? JSON.parse(orderEl.textContent) : [];
      if (order.length) {
        /* collection_order is an array of objects: [{id, title, handle, ...}, ...] */
        const orderMap = new Map(
          order.map((item, i) => {
            const name = (typeof item === 'string' ? item : item.title || '').toLowerCase();
            return [name, i];
          })
        );
        sortedCollections = [...allCollections].sort((a, b) => {
          const ia = orderMap.has(a.toLowerCase()) ? orderMap.get(a.toLowerCase()) : 9999;
          const ib = orderMap.has(b.toLowerCase()) ? orderMap.get(b.toLowerCase()) : 9999;
          return ia - ib || a.localeCompare(b);
        });
      } else {
        sortedCollections = [...allCollections].sort();
      }
    } catch {
      sortedCollections = [...allCollections].sort();
    }
    const sortedMachines = [...allMachines].sort();

    /* ── Populate dropdowns ── */
    function buildCheckboxes(container, options, group) {
      if (!container) return;
      container.innerHTML = '';
      if (options.length === 0) {
        container.innerHTML = '<span class="vp-spp__dropdown-empty">No options</span>';
        return;
      }
      options.forEach((opt) => {
        const label = document.createElement('label');
        label.className = 'vp-spp__dropdown-item';
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.value = opt;
        cb.className = 'vp-spp__dropdown-cb';
        cb.dataset.group = group;
        const span = document.createElement('span');
        span.textContent = opt;
        label.appendChild(cb);
        label.appendChild(span);
        container.appendChild(label);
      });
    }

    buildCheckboxes(dMachinesDropdown, sortedMachines, 'machines');
    buildCheckboxes(dCollDropdown, sortedCollections, 'collections');
    buildCheckboxes(mMachinesPanelList, sortedMachines, 'machines');
    buildCheckboxes(mCollPanelList, sortedCollections, 'collections');

    /* ── Filter logic ── */
    function applyFilter() {
      const kw = state.keywords.toLowerCase().trim();
      let visibleCount = 0;

      items.forEach((item) => {
        const meta = productMeta.get(item.dataset.productId);
        if (!meta) {
          item.hidden = true;
          return;
        }

        /* Keyword match */
        const kwMatch = !kw || meta.title.includes(kw);

        /* Machines match (OR within selected) */
        const machineMatch =
          state.machines.size === 0 ||
          meta.machines.some((m) => state.machines.has(m));

        /* Collections match (OR within selected) */
        const collMatch =
          state.collections.size === 0 ||
          meta.collections.some((c) => state.collections.has(c));

        /* AND between segments */
        const visible = kwMatch && machineMatch && collMatch;
        item.hidden = !visible;
        if (visible) visibleCount++;
      });

      /* Show/hide no-results message */
      if (noResults) {
        noResults.hidden = visibleCount > 0;
      }
      if (grid) {
        grid.hidden = visibleCount === 0;
      }

      updateClearButtons();
      updateMobileSummary();
    }

    function hasAnyFilter() {
      return (
        state.keywords.trim() !== '' ||
        state.machines.size > 0 ||
        state.collections.size > 0
      );
    }

    function updateClearButtons() {
      const show = hasAnyFilter();
      if (dClear) dClear.hidden = !show;
      if (mClear) mClear.hidden = !show;
    }

    function clearAll() {
      state.keywords = '';
      state.machines.clear();
      state.collections.clear();

      if (dKeywords) dKeywords.value = '';
      if (mKeywords) mKeywords.value = '';

      /* Clear filter inputs (desktop dropdowns + mobile panel search) */
      $$('.vp-spp__seg-filter, .vp-spp__panel-search-input', root).forEach((input) => {
        input.value = '';
      });

      $$('.vp-spp__dropdown-cb', root).forEach((cb) => {
        cb.checked = false;
      });

      /* Reset hidden items */
      $$('.vp-spp__dropdown-item, .vp-spp__dropdown-heading', root).forEach((el) => {
        el.hidden = false;
      });

      updateSegmentDisplays();
      applyFilter();
    }

    /* ── Desktop filter inputs ── */
    const dMachinesFilter = $('.vp-spp__seg-filter--machines', root);
    const dCollFilter = $('.vp-spp__seg-filter--collections', root);

    /* ── Mobile trigger labels ── */
    const mMachinesLabel = mMachinesTrigger ? mMachinesTrigger.querySelector('.vp-spp__drawer-seg-label') : null;
    const mCollLabel = mCollTrigger ? mCollTrigger.querySelector('.vp-spp__drawer-seg-label') : null;

    /* ── Segment display text ── */
    function updateSegmentDisplays() {
      /* Desktop */
      if (dMachinesFilter) {
        const n = state.machines.size;
        dMachinesFilter.placeholder = n > 0 ? `${n} machine${n > 1 ? 's' : ''}` : 'Machines';
        if (dMachinesTrigger) dMachinesTrigger.classList.toggle('vp-spp__seg-trigger--active', n > 0);
      }
      if (dCollFilter) {
        const n = state.collections.size;
        dCollFilter.placeholder = n > 0 ? `${n} collection${n > 1 ? 's' : ''}` : 'Collections';
        if (dCollTrigger) dCollTrigger.classList.toggle('vp-spp__seg-trigger--active', n > 0);
      }
      /* Mobile */
      if (mMachinesLabel) {
        const n = state.machines.size;
        mMachinesLabel.textContent = n > 0 ? `${n} machine${n > 1 ? 's' : ''}` : 'Machines';
        if (mMachinesTrigger) mMachinesTrigger.classList.toggle('vp-spp__drawer-seg-trigger--active', n > 0);
      }
      if (mCollLabel) {
        const n = state.collections.size;
        mCollLabel.textContent = n > 0 ? `${n} collection${n > 1 ? 's' : ''}` : 'Collections';
        if (mCollTrigger) mCollTrigger.classList.toggle('vp-spp__drawer-seg-trigger--active', n > 0);
      }
    }

    function updateMobileSummary() {
      if (!mInput) return;
      const parts = [];
      if (state.keywords.trim()) parts.push(state.keywords.trim());
      if (state.machines.size > 0)
        parts.push(`${state.machines.size} machine${state.machines.size > 1 ? 's' : ''}`);
      if (state.collections.size > 0)
        parts.push(
          `${state.collections.size} collection${state.collections.size > 1 ? 's' : ''}`
        );
      mInput.value = parts.join(' · ');
    }

    /* ── Sync state between desktop & mobile checkboxes ── */
    function syncCheckboxes(group, value, checked) {
      $$(`.vp-spp__dropdown-cb[data-group="${group}"][value="${CSS.escape(value)}"]`, root).forEach(
        (cb) => {
          cb.checked = checked;
        }
      );
    }

    /* ── Checkbox change handler ── */
    function onCheckboxChange(e) {
      const cb = e.target;
      if (!cb.classList.contains('vp-spp__dropdown-cb')) return;
      const group = cb.dataset.group;
      const value = cb.value;
      const set = group === 'machines' ? state.machines : state.collections;

      if (cb.checked) set.add(value);
      else set.delete(value);

      syncCheckboxes(group, value, cb.checked);
      updateSegmentDisplays();
      applyFilter();
    }

    root.addEventListener('change', onCheckboxChange);

    /* ── Desktop keywords ── */
    if (dKeywords) {
      dKeywords.addEventListener('input', () => {
        state.keywords = dKeywords.value;
        if (mKeywords) mKeywords.value = dKeywords.value;
        applyFilter();
      });
    }

    /* ── Mobile keywords ── */
    if (mKeywords) {
      mKeywords.addEventListener('input', () => {
        state.keywords = mKeywords.value;
        if (dKeywords) dKeywords.value = mKeywords.value;
        applyFilter();
      });
    }

    /* ── Filter dropdown options by typing ── */
    function filterDropdownOptions(dropdown, query) {
      if (!dropdown) return;
      const q = query.toLowerCase().trim();
      const items = $$('.vp-spp__dropdown-item', dropdown);
      const headings = $$('.vp-spp__dropdown-heading', dropdown);

      let visibleCount = 0;
      items.forEach((item) => {
        const text = (item.querySelector('span')?.textContent || '').toLowerCase();
        const show = q === '' || text.includes(q);
        item.hidden = !show;
        if (show) visibleCount++;
      });

      /* Hide group headings if all their items are hidden */
      headings.forEach((heading) => {
        let sibling = heading.nextElementSibling;
        let anyVisible = false;
        while (sibling && !sibling.classList.contains('vp-spp__dropdown-heading')) {
          if (sibling.classList.contains('vp-spp__dropdown-item') && !sibling.hidden) {
            anyVisible = true;
          }
          sibling = sibling.nextElementSibling;
        }
        heading.hidden = !anyVisible;
      });

      /* Show/hide no-match message */
      let noMatch = dropdown.querySelector('.vp-spp__dropdown-no-match');
      if (visibleCount === 0 && q !== '') {
        if (!noMatch) {
          noMatch = document.createElement('span');
          noMatch.className = 'vp-spp__dropdown-no-match';
          dropdown.appendChild(noMatch);
        }
        noMatch.textContent = 'No results for \u201c' + query.trim() + '\u201d';
        noMatch.hidden = false;
      } else if (noMatch) {
        noMatch.hidden = true;
      }
    }

    function setupFilterInput(filterInput, trigger, dropdown) {
      if (!filterInput || !trigger || !dropdown) return;

      filterInput.addEventListener('input', () => {
        filterDropdownOptions(dropdown, filterInput.value);
        if (!dropdown.classList.contains('is-open')) {
          closeAllDropdowns(dropdown);
          dropdown.classList.add('is-open');
          trigger.classList.add('is-open');
        }
      });

      filterInput.addEventListener('focus', () => {
        closeAllDropdowns(dropdown);
        filterDropdownOptions(dropdown, filterInput.value);
        dropdown.classList.add('is-open');
        trigger.classList.add('is-open');
      });
    }

    /* ── Desktop dropdown toggle ── */
    function closeAllDropdowns(except) {
      $$('.vp-spp__dropdown', root).forEach((d) => {
        if (d === except) return;
        d.classList.remove('is-open');
      });
      $$('.vp-spp__seg-trigger', root).forEach((t) => {
        /* Skip the trigger that contains the active dropdown */
        if (except && t.closest('.vp-spp__segment')?.contains(except)) return;
        t.classList.remove('is-open');
      });
      /* Clear filter inputs and reset visibility on close (skip the active one) */
      $$('.vp-spp__seg-filter', root).forEach((input) => {
        if (except && input.closest('.vp-spp__segment')?.contains(except)) return;
        input.value = '';
      });
      $$('.vp-spp__dropdown', root).forEach((d) => {
        if (d === except) return;
        $$('.vp-spp__dropdown-item, .vp-spp__dropdown-heading', d).forEach((el) => {
          el.hidden = false;
        });
        const noMatch = d.querySelector('.vp-spp__dropdown-no-match');
        if (noMatch) noMatch.hidden = true;
      });
    }

    function toggleDesktopDropdown(trigger, dropdown) {
      if (!trigger || !dropdown) return;
      /* Clicking chevron toggles */
      const chevron = trigger.querySelector('.vp-spp__seg-chevron');
      if (chevron) {
        chevron.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = dropdown.classList.contains('is-open');
          closeAllDropdowns();
          if (!isOpen) {
            dropdown.classList.add('is-open');
            trigger.classList.add('is-open');
            const filterInput = trigger.querySelector('.vp-spp__seg-filter');
            if (filterInput) filterInput.focus();
          }
        });
      }
    }

    toggleDesktopDropdown(dMachinesTrigger, dMachinesDropdown);
    toggleDesktopDropdown(dCollTrigger, dCollDropdown);

    setupFilterInput(dMachinesFilter, dMachinesTrigger, dMachinesDropdown);
    setupFilterInput(dCollFilter, dCollTrigger, dCollDropdown);

    /* Close dropdowns on outside click */
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.vp-spp__segment')) {
        closeAllDropdowns();
      }
    });

    /* ── Mobile full-screen panel open / close ── */
    function openPanel(panel) {
      if (!panel) return;
      panel.classList.remove('is-closing');
      panel.classList.add('is-open');
    }

    function closePanel(panel) {
      if (!panel || !panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      void panel.offsetHeight;
      panel.classList.add('is-closing');
      panel.addEventListener('animationend', () => {
        panel.classList.remove('is-closing');
      }, { once: true });
      /* Clear search input and reset list visibility */
      const searchInput = panel.querySelector('.vp-spp__panel-search-input');
      if (searchInput) searchInput.value = '';
      const list = panel.querySelector('.vp-spp__panel-list');
      if (list) {
        $$('.vp-spp__dropdown-item, .vp-spp__dropdown-heading', list).forEach((el) => {
          el.hidden = false;
        });
        const noMatch = list.querySelector('.vp-spp__dropdown-no-match');
        if (noMatch) noMatch.hidden = true;
      }
    }

    function hasOpenPanel() {
      return !!$('.vp-spp__panel.is-open', root);
    }

    /* Wire up trigger → panel */
    function setupPanelTrigger(trigger, panel) {
      if (!trigger || !panel) return;
      trigger.addEventListener('click', () => openPanel(panel));
    }

    setupPanelTrigger(mMachinesTrigger, mMachinesPanel);
    setupPanelTrigger(mCollTrigger, mCollPanel);

    /* Wire up back buttons */
    $$('.vp-spp__panel-back', root).forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('.vp-spp__panel');
        closePanel(panel);
      });
    });

    /* Wire up panel search inputs */
    function setupPanelSearch(searchInput, list) {
      if (!searchInput || !list) return;
      searchInput.addEventListener('input', () => {
        filterDropdownOptions(list, searchInput.value);
      });
    }

    setupPanelSearch(mMachinesPanelSearch, mMachinesPanelList);
    setupPanelSearch(mCollPanelSearch, mCollPanelList);

    /* ── Mobile drawer open / close ── */
    function openDrawer() {
      if (!drawer || !overlay) return;
      drawer.classList.remove('is-closing');
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
      if (!drawer || !overlay) return;
      /* Close any open panels first */
      $$('.vp-spp__panel.is-open', root).forEach((p) => closePanel(p));
      overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      /* Force reflow so browser sees the state change before starting close animation */
      void drawer.offsetHeight;
      drawer.classList.add('is-closing');
      document.body.style.overflow = '';
      drawer.addEventListener('animationend', () => {
        drawer.classList.remove('is-closing');
      }, { once: true });
    }

    if (mInput) mInput.addEventListener('focus', openDrawer);
    if (mInput) mInput.addEventListener('click', openDrawer);
    if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', () => {
      closeDrawer();
    });

    /* ── Clear buttons ── */
    if (dClear) dClear.addEventListener('click', clearAll);
    if (mClear) mClear.addEventListener('click', clearAll);

    /* ── Sticky: hide/show H1 ── */
    if (stickyWrapper && title) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          /* When title is not intersecting (scrolled past), add stuck class */
          stickyWrapper.classList.toggle('is-stuck', !entry.isIntersecting);
        },
        { threshold: 0, rootMargin: `-${parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '80', 10) + 16}px 0px 0px 0px` }
      );
      observer.observe(title);
    }

    /* ── Sticky: adapt top offset when header appears/hides ── */
    const headerComponent = document.querySelector('header-component');
    if (headerComponent && stickyWrapper) {
      const updateStickyTop = () => {
        const state = headerComponent.dataset.stickyState;
        const headerH = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height') || '0');
        if (state === 'active') {
          stickyWrapper.style.top = `${Math.ceil(headerH) - 1}px`;
        } else {
          stickyWrapper.style.top = '0px';
        }
      };

      /* Observe data-sticky-state changes on the header */
      const headerObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.attributeName === 'data-sticky-state') {
            updateStickyTop();
            break;
          }
        }
      });
      headerObserver.observe(headerComponent, { attributes: true, attributeFilter: ['data-sticky-state'] });

      /* Initial check */
      updateStickyTop();
    }

    /* ── Initial state ── */
    updateClearButtons();
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
